import { type SxProps, type Theme } from '@mui/material/styles';
import { BORDER, ELEVATION, MOTION, RADIUS, SPACE, SURFACE, TYPE } from '../foundation';

/**
 * Design System V2 — the canonical kit.
 * Values alias design-system/foundation.ts; keep names stable for callers.
 */

export const DS2_SPACE = SPACE;

export const DS2_RADIUS = {
  sm: RADIUS.chip,
  md: RADIUS.control,
  lg: RADIUS.card,
  xl: RADIUS.dialog,
} as const;

export const DS2_MOTION = {
  fast: MOTION.fast,
  normal: MOTION.normal,
  slow: MOTION.slow,
} as const;

/** Typography — hierarchy via size, not bold */
export const DS2_TYPE = {
  pageTitle: TYPE.pageTitle,
  sectionTitle: TYPE.sectionTitle,
  subheading: TYPE.subheading,
  label: { ...TYPE.label, color: 'var(--cockpit-secondary)' },
  overline: { ...TYPE.overline, color: 'var(--cockpit-tertiary)' },
  metric: TYPE.metric,
  metricSm: TYPE.metricSm,
  body: { ...TYPE.body, color: 'var(--cockpit-secondary)' },
  caption: { ...TYPE.caption, color: 'var(--cockpit-secondary)' },
} as const;

export type SurfaceLevel =
  | 'canvas'
  | 'flat'
  | 'raised'
  | 'inset'
  | 'diagram'
  | 'focused'
  | 'interactive';

// CSS-var-backed so every Panel/Surface flips with light/dark mode.
// `diagram` is the one deliberate exception: an always-dark stage for graphs.
const SURFACE_BG: Record<SurfaceLevel, string> = {
  canvas: 'transparent',
  flat: SURFACE.panel,
  raised: SURFACE.panel,
  inset: SURFACE.card,
  diagram: SURFACE.stage,
  focused: SURFACE.panel,
  interactive: SURFACE.panel,
};

/**
 * One card recipe for the whole app: panel background, hairline border,
 * 12px radius, soft elevation. `canvas` is chrome-less; `inset` is a
 * recessed well inside a card.
 */
export function surfaceSx(level: SurfaceLevel = 'flat'): SxProps<Theme> {
  const chromeless = level === 'canvas';
  const inset = level === 'inset';
  return {
    bgcolor: SURFACE_BG[level],
    border: chromeless ? 'none' : `1px solid ${inset ? BORDER.subtle : BORDER.default}`,
    boxShadow: chromeless || inset || level === 'diagram' ? 'none' : ELEVATION.sm,
    borderRadius: `${inset ? RADIUS.control : RADIUS.card}px`,
    transition: `background-color ${MOTION.normal}, box-shadow ${MOTION.normal}, border-color ${MOTION.normal}`,
    ...(level === 'interactive' && {
      cursor: 'pointer',
      '&:hover': { borderColor: BORDER.strong, boxShadow: ELEVATION.md },
    }),
    ...(level === 'focused' && { borderColor: 'var(--accent)' }),
  };
}

export function separatorSx(): SxProps<Theme> {
  return {
    borderBottom: `1px solid ${BORDER.subtle}`,
  };
}

/** @deprecated surfaceSx is bordered now — kept for existing imports */
export function borderedSurfaceSx(level: SurfaceLevel = 'raised'): SxProps<Theme> {
  return surfaceSx(level);
}

export function accentMetricSx(color: string): SxProps<Theme> {
  return {
    ...surfaceSx('raised'),
    bgcolor: `color-mix(in srgb, ${color} 6%, var(--panel-bg))`,
    '&:hover': {
      bgcolor: `color-mix(in srgb, ${color} 10%, var(--panel-bg))`,
    },
  };
}
